import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Mic, MicOff, Send, Trash2, Loader2, ParkingSquare, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const PRIORITY_COLORS = {
  high: 'bg-red-500/10 text-red-600 border-red-500/30',
  medium: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
  low: 'bg-gray-500/10 text-gray-600 border-gray-500/30',
};

const STATUS_COLORS = {
  open: 'bg-blue-500/10 text-blue-600',
  resolved: 'bg-green-500/10 text-green-600',
  carried_forward: 'bg-purple-500/10 text-purple-600',
  dismissed: 'bg-gray-500/10 text-gray-500',
};

export default function ParkingLotTab({ meetingId, members }) {
  const { api, member, isChair } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItemText, setNewItemText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);

  const fetchItems = useCallback(async () => {
    try {
      const response = await api().get(`/meetings/${meetingId}/parking-lot`);
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch parking lot:', error);
      toast.error('Failed to load parking lot');
    } finally {
      setLoading(false);
    }
  }, [api, meetingId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Initialize Web Speech API
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const current = event.resultIndex;
        const result = event.results[current];
        setTranscript(result[0].transcript);
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
        toast.error('Voice recognition failed. Please try again or type your item.');
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      toast.error('Voice recognition not supported in this browser');
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      setTranscript('');
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };

  const handleAddItem = async (text) => {
    const itemText = text || newItemText;
    if (!itemText.trim()) {
      toast.error('Please enter some text');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api().post(`/meetings/${meetingId}/parking-lot`, {
        text: itemText.trim(),
        priority: 'medium',
        status: 'open',
      });
      setItems([response.data, ...items]);
      setNewItemText('');
      setTranscript('');
      toast.success('Item added to parking lot');
    } catch (error) {
      toast.error('Failed to add item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateItem = async (itemId, updates) => {
    try {
      const item = items.find(i => i.id === itemId);
      const response = await api().put(`/meetings/${meetingId}/parking-lot/${itemId}`, {
        ...item,
        ...updates,
      });
      setItems(items.map(i => i.id === itemId ? response.data : i));
      toast.success('Item updated');
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await api().delete(`/meetings/${meetingId}/parking-lot/${itemId}`);
      setItems(items.filter(i => i.id !== itemId));
      toast.success('Item deleted');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const getMemberName = (memberId) => {
    const m = members.find(mem => mem.id === memberId);
    return m?.name || 'Unknown';
  };

  const openItems = items.filter(i => i.status === 'open');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parking-lot-tab">
      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <Badge variant="outline" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          {openItems.length} open
        </Badge>
        <Badge variant="outline" className="gap-1">
          {items.length} total
        </Badge>
      </div>

      {/* Add new item */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Textarea
                placeholder="Type your parking lot item..."
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                className="min-h-[80px] resize-none"
                data-testid="parking-lot-input"
              />
            </div>
          </div>

          {/* Voice input */}
          {transcript && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Voice transcript:</p>
              <p className="font-medium">{transcript}</p>
            </div>
          )}

          <div className="flex items-center justify-between">
            <Button
              variant={isRecording ? "destructive" : "outline"}
              size="sm"
              onClick={toggleRecording}
              className={`gap-2 ${isRecording ? 'recording-indicator' : ''}`}
              data-testid="voice-input-btn"
            >
              {isRecording ? (
                <>
                  <MicOff className="h-4 w-4" />
                  Stop Recording
                </>
              ) : (
                <>
                  <Mic className="h-4 w-4" />
                  Voice Input
                </>
              )}
            </Button>

            <div className="flex gap-2">
              {transcript && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleAddItem(transcript)}
                  disabled={submitting}
                  data-testid="add-transcript-btn"
                >
                  Add Transcript
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => handleAddItem()}
                disabled={submitting || !newItemText.trim()}
                className="gap-2"
                data-testid="add-item-btn"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Add Item
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items list */}
      {items.length === 0 ? (
        <Card className="p-12">
          <div className="empty-state">
            <ParkingSquare className="h-12 w-12 mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold mb-2">Parking lot is empty</h3>
            <p className="text-muted-foreground">
              Add items that come up during the meeting but can't be addressed right away.
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((item, index) => (
            <Card key={item.id} className="hover:shadow-md transition-shadow" data-testid={`parking-lot-item-${index}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium ${item.status === 'dismissed' ? 'line-through text-muted-foreground' : ''}`}>
                      {item.text}
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <Badge variant="outline" className={STATUS_COLORS[item.status]}>
                        {item.status.replace('_', ' ')}
                      </Badge>
                      
                      <Badge variant="outline" className={PRIORITY_COLORS[item.priority]}>
                        {item.priority}
                      </Badge>
                      
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                      </span>
                      
                      <span className="text-xs text-muted-foreground">
                        by {getMemberName(item.contributor_id)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {isChair && (
                      <>
                        <Select
                          value={item.priority}
                          onValueChange={(value) => handleUpdateItem(item.id, { priority: value })}
                        >
                          <SelectTrigger className="h-8 w-24" data-testid={`priority-select-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                          </SelectContent>
                        </Select>

                        <Select
                          value={item.status}
                          onValueChange={(value) => handleUpdateItem(item.id, { status: value })}
                        >
                          <SelectTrigger className="h-8 w-32" data-testid={`status-select-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="carried_forward">Carry Forward</SelectItem>
                            <SelectItem value="dismissed">Dismissed</SelectItem>
                          </SelectContent>
                        </Select>
                      </>
                    )}

                    {(isChair || item.contributor_id === member?.id) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteItem(item.id)}
                        data-testid={`delete-item-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
